/*
 * replaceText — orchestrator for swapping the text on the currently-selected
 * UE node to a new string.
 *
 * Mirrors lib/replaceImage.js. Routes the write through the Universal Editor
 * host's editorActions API:
 *   1. editorActions.details(editable) — fetch the component model so we can
 *      pick the right text-bearing property name (text / title / linkText / ...).
 *   2. editorActions.update(target, patch) — JSON Patch (RFC 6902) `op: replace`
 *      against that property.
 *
 * The host owns the network round-trip + canvas postMessage, so a successful
 * await means the canvas has already repainted with the new copy.
 */

// Common text-bearing property names on AEM components, ordered by likelihood.
const TEXT_PROPERTY_CANDIDATES = [
  'text',
  'jcr:title',
  'title',
  'linkText',
  'description',
  'subtitle',
  'label',
  'cta',
  'caption',
];

// Skip these in the fallback "any string" pass — they hold paths/ids/urls, not
// user-visible copy.
const SKIP_PROPS = new Set([
  'fileReference', 'image', 'src', 'imagePath', 'imageReference',
  'id', 'href', 'url', 'link', 'linkURL', 'linkUrl',
  'name', 'type', 'resourceType',
]);

export function findTextPropertyName(modelOrJson) {
  if (!modelOrJson || typeof modelOrJson !== 'object') return '';

  const candidates = [
    modelOrJson.data,
    modelOrJson.properties,
    modelOrJson.model,
    modelOrJson,
  ].filter((x) => x && typeof x === 'object');

  // Pass 1: known names whose current value is a non-empty string (most
  // likely the property in active use on this component).
  for (const obj of candidates) {
    for (const name of TEXT_PROPERTY_CANDIDATES) {
      const v = obj[name];
      if (typeof v === 'string' && v.length > 0) return name;
    }
  }
  // Pass 2: known names even if the value is empty / not-set.
  for (const obj of candidates) {
    for (const name of TEXT_PROPERTY_CANDIDATES) {
      if (Object.prototype.hasOwnProperty.call(obj, name)) return name;
    }
  }
  // Pass 3: any plausibly-text string property — skip jcr/sling/cq metadata,
  // skip image/id/link-like keys, prefer the longest value as it's most
  // likely user-visible copy.
  let best = '';
  let bestLen = -1;
  for (const obj of candidates) {
    for (const [k, v] of Object.entries(obj)) {
      if (k.startsWith('jcr:') || k.startsWith('sling:') || k.startsWith('cq:')) continue;
      if (SKIP_PROPS.has(k)) continue;
      if (typeof v !== 'string') continue;
      if (v.length > bestLen) {
        best = k;
        bestLen = v.length;
      }
    }
  }
  return best;
}

/**
 * Given an editorActions.details() response, return the current text-bearing
 * property's name and value, e.g. `{ name: 'text', value: 'Get Started' }`.
 * Returns empty strings if no text-bearing property is found or the value is
 * absent / non-string.
 *
 * @param {object} detailsResponse - the editorActions.details(editable) result
 * @returns {{name: string, value: string}}
 */
export function extractTextFromDetails(detailsResponse) {
  const empty = { name: '', value: '' };
  if (!detailsResponse || typeof detailsResponse !== 'object') return empty;
  const name = findTextPropertyName(detailsResponse);
  if (!name) return empty;
  const containers = [
    detailsResponse.data,
    detailsResponse.properties,
    detailsResponse.model,
    detailsResponse,
  ].filter((x) => x && typeof x === 'object');
  for (const obj of containers) {
    const v = obj[name];
    if (typeof v === 'string') return { name, value: v };
  }
  return { name, value: '' };
}

/**
 * @param {object} args
 * @param {string} args.text     - new text to apply to the selected node
 * @param {object} args.ue       - the useUEContext return value (must have
 *                                 conn and selection.editable)
 * @param {(msg:string)=>void} [args.onStatus]
 * @returns {Promise<{ok:boolean, error?:string, propertyName?:string}>}
 */
export async function replaceText({ text, ue, onStatus }) {
  const say = (m) => { if (typeof onStatus === 'function') onStatus(m); };

  if (typeof text !== 'string') return { ok: false, error: 'no text provided' };
  if (!ue || !ue.conn) return { ok: false, error: 'UE not ready' };

  const editorActions = ue.conn?.host?.editorActions;
  if (!editorActions || typeof editorActions.update !== 'function') {
    return { ok: false, error: 'editorActions.update not available on this UE host' };
  }

  const editable = ue.selection?.editable;
  if (!editable) return { ok: false, error: 'nothing selected in the canvas' };

  say('Inspecting…');
  let propertyName = '';
  if (typeof editorActions.details === 'function') {
    try {
      const details = await editorActions.details(editable);
      propertyName = findTextPropertyName(details);
      console.log('[DemoPilot] editorActions.details ok, text propertyName probe:', propertyName);
    } catch (err) {
      console.warn('[DemoPilot] editorActions.details failed:', err);
    }
  }
  if (!propertyName) propertyName = 'text';

  say('Saving…');
  try {
    await editorActions.update({
      target: { editable },
      patch: [{ op: 'replace', path: `/${propertyName}`, value: text }],
    });
  } catch (err) {
    console.error('[DemoPilot] editorActions.update (text) failed:', err);
    return { ok: false, error: err?.message || 'update failed', propertyName };
  }

  say('Replaced');
  return { ok: true, propertyName };
}
